import { useState, useMemo } from "react";

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import { AlertTriangle, Archive, X, RefreshCw } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";
import { supabase } from "@/integrations/supabase/client";

interface StaleBranch {
  session_id: string;
  session_name: string;
  branch_name: string | null;
  is_branch: boolean;
  parent_session_id: string | null;
  last_activity_at: string;
  days_inactive: number;
  new_messages: number;
  memories_created: number;
}

interface StaleBranchDetectorProps {
  projectId: string;
  daysThreshold?: number;
  onBranchesArchived: () => void;
}

export function StaleBranchDetector({
  projectId,
  daysThreshold = 7,
  onBranchesArchived
}: StaleBranchDetectorProps) {
  const queryClient = useQueryClient();
  // Track what user has DESELECTED instead of what's selected
  // This inverts the state, eliminating sync issues
  const [deselectedBranches, setDeselectedBranches] = useState<Set<string>>(new Set());
  const [dismissed, setDismissed] = useState(false);

  const { data: staleBranches = [], isLoading, refetch } = useQuery<StaleBranch[]>({
    queryKey: ['staleBranches', projectId, daysThreshold],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('get_stale_branches', {
        p_project_id: projectId,
        p_days_threshold: daysThreshold
      });
      if (error) {
        toast.error('Failed to detect stale branches');
        throw error;
      }
      return data || [];
    }
  });

  // Derive selected branches from all branches minus deselected ones
  // This automatically updates when data changes - no useEffect needed!
  const selectedBranches = useMemo(() => {
    const allIds = new Set(staleBranches.map(b => b.session_id));
    deselectedBranches.forEach(id => allIds.delete(id));
    return allIds;
  }, [staleBranches, deselectedBranches]);

  const archiveMutation = useMutation({
    mutationFn: async (sessionIds: string[]) => {
      const { data, error } = await supabase.rpc('bulk_archive_branches', {
        p_session_ids: sessionIds,
        p_reason: `Automatically archived after ${daysThreshold}+ days of inactivity`
      });

      if (error) throw error;
      return data;
    }
  });

  const handleBulkArchive = async () => {
    if (selectedBranches.size === 0) {
      toast.error('No branches selected');
      return;
    }
    
    try {
      const result = await archiveMutation.mutateAsync(Array.from(selectedBranches));
      const data = result[0];
      
      toast.success(`Archived ${data.archived_count} branch${data.archived_count !== 1 ? 'es' : ''}`);
      
      if (data.failed_ids && data.failed_ids.length > 0) {
        toast.warning(`Failed to archive ${data.failed_ids.length} branch${data.failed_ids.length !== 1 ? 'es' : ''}`);
      }

      queryClient.invalidateQueries({ queryKey: ['staleBranches'] });
      setDeselectedBranches(new Set());
      onBranchesArchived();
    } catch (error: any) {
      console.error('Error bulk archiving:', error);
      toast.error('Failed to archive branches');
    }
  };

  const toggleBranch = (branchId: string) => {
    const newDeselected = new Set(deselectedBranches);
    if (selectedBranches.has(branchId)) {
      // Currently selected, so deselect it
      newDeselected.add(branchId);
    } else {
      // Currently deselected, so remove from deselected
      newDeselected.delete(branchId);
    }
    setDeselectedBranches(newDeselected);
  };

  const toggleAll = () => {
    if (selectedBranches.size === staleBranches.length) {
      // All selected, so deselect all
      setDeselectedBranches(new Set(staleBranches.map(b => b.session_id)));
    } else {
      // Some/none selected, so select all (clear deselections)
      setDeselectedBranches(new Set());
    }
  };

  if (isLoading) {
    return (
      <Card className="p-4 border-warning/50 bg-warning/5">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <RefreshCw className="h-4 w-4 animate-spin" />
          Detecting stale branches...
        </div>
      </Card>
    );
  }

  if (staleBranches.length === 0 || dismissed) {
    return null;
  }

  return (
    <Card className="p-4 border-warning/50 bg-warning/5">
      <div className="space-y-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-warning mt-0.5" />
            <div>
              <h3 className="font-medium text-sm">Stale Branches Detected</h3>
              <p className="text-xs text-muted-foreground mt-1">
                {staleBranches.length} branch{staleBranches.length !== 1 ? 'es' : ''} inactive for {daysThreshold}+ days
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="h-6 w-6 p-0"
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        <div className="space-y-2 max-h-60 overflow-y-auto">
          <div className="flex items-center gap-2 pb-2 border-b">
            <Checkbox
              checked={selectedBranches.size === staleBranches.length}
              onCheckedChange={toggleAll}
              id="select-all"
            />
            <label
              htmlFor="select-all"
              className="text-xs font-medium cursor-pointer"
            >
              Select All ({selectedBranches.size}/{staleBranches.length})
            </label>
          </div>

          {staleBranches.map((branch) => (
            <div
              key={branch.session_id}
              className="flex items-start gap-2 p-2 rounded hover:bg-muted/50 transition-colors"
            >
              <Checkbox
                checked={selectedBranches.has(branch.session_id)}
                onCheckedChange={() => toggleBranch(branch.session_id)}
                id={branch.session_id}
              />
              <label
                htmlFor={branch.session_id}
                className="flex-1 cursor-pointer space-y-1"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {branch.branch_name || branch.session_name}
                  </span>
                  <Badge variant="secondary" className="text-xs">
                    {branch.days_inactive}d inactive
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground">
                  Last active {formatDistanceToNow(new Date(branch.last_activity_at), { addSuffix: true })} · 
                  {branch.new_messages} message{branch.new_messages !== 1 ? 's' : ''} · 
                  {branch.memories_created} memor{branch.memories_created !== 1 ? 'ies' : 'y'}
                </div>
              </label>
            </div>
          ))}
        </div>

        <div className="flex gap-2 pt-2 border-t">
          <Button
            onClick={handleBulkArchive}
            disabled={selectedBranches.size === 0 || archiveMutation.isPending}
            size="sm"
            variant="default"
            className="flex-1"
          >
            <Archive className="h-3 w-3 mr-2" />
            {archiveMutation.isPending ? 'Archiving...' : `Archive Selected (${selectedBranches.size})`}
          </Button>
          <Button
            onClick={() => refetch()}
            disabled={isLoading || archiveMutation.isPending}
            size="sm"
            variant="outline"
          >
            <RefreshCw className={`h-3 w-3 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>
    </Card>
  );
}
